import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Maximize2, MessageSquare, Minus, SquarePen, X } from "lucide-react";
import { useAuth } from "../scripts/AuthContext";
import { useOrgFilter } from "../scripts/OrgFilterContext";
import { useDmDrawer } from "../scripts/DmDrawerContext";
import { useDmSocket } from "../scripts/DmSocketContext";
import { useDmConversations } from "../hooks/useDmConversations";
import {
  DM_EMPTY_IDLE_MS,
  deleteDmConversation,
  ensureDmNotes,
  setDmMuted,
} from "@/lib/dmApi";
import { setConversationMutedLocal } from "@/lib/dmMuteCache";
import DmConversationSections from "./dm/DmConversationSections";
import DmThread from "./dm/DmThread";
import NewDmChatDialog from "./dm/NewDmChatDialog";
import { getFullName } from "@/lib/userUtils";
import { resolvePresence } from "@/lib/presence";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const DRAWER_SIZE_KEY = "dm-drawer-size";
const DEFAULT_DRAWER_SIZE = { w: 580, h: 440 };
const MIN_DRAWER_W = 420;
const MIN_DRAWER_H = 300;

function loadDrawerSize() {
  try {
    const raw = localStorage.getItem(DRAWER_SIZE_KEY);
    if (!raw) return DEFAULT_DRAWER_SIZE;
    const parsed = JSON.parse(raw);
    const w = Number(parsed?.w);
    const h = Number(parsed?.h);
    if (!Number.isFinite(w) || !Number.isFinite(h)) return DEFAULT_DRAWER_SIZE;
    return { w, h };
  } catch {
    return DEFAULT_DRAWER_SIZE;
  }
}

function clampDrawerSize(w, h) {
  const maxW = Math.max(
    MIN_DRAWER_W,
    (typeof window !== "undefined" ? window.innerWidth : 1200) - 32
  );
  const maxH = Math.max(
    MIN_DRAWER_H,
    (typeof window !== "undefined" ? window.innerHeight : 800) - 32
  );
  return {
    w: Math.min(maxW, Math.max(MIN_DRAWER_W, Math.round(w))),
    h: Math.min(maxH, Math.max(MIN_DRAWER_H, Math.round(h))),
  };
}

function isFullMessagesPath(pathname) {
  return (
    pathname === "/messages" ||
    /^\/o\/[^/]+\/messages(\/|$)/.test(pathname)
  );
}

/** Keep wheel/trackpad scroll inside the drawer instead of chaining to the page. */
function useContainDrawerScroll(ref, enabled) {
  useEffect(() => {
    const node = ref.current;
    if (!enabled || !node) return;

    const onWheel = (e) => {
      let el = e.target;
      while (el && el !== node) {
        if (el instanceof HTMLElement) {
          const { overflowY } = window.getComputedStyle(el);
          const scrollable =
            (overflowY === "auto" ||
              overflowY === "scroll" ||
              overflowY === "overlay") &&
            el.scrollHeight > el.clientHeight;
          if (scrollable) {
            const atTop = el.scrollTop <= 0;
            const atBottom =
              el.scrollTop + el.clientHeight >= el.scrollHeight - 1;
            if ((e.deltaY < 0 && atTop) || (e.deltaY > 0 && atBottom)) {
              e.preventDefault();
            }
            return;
          }
        }
        el = el.parentElement;
      }
      // Wheel over chrome / non-scrollable drawer area — block the page.
      e.preventDefault();
    };

    node.addEventListener("wheel", onWheel, { passive: false });
    return () => node.removeEventListener("wheel", onWheel);
  }, [enabled, ref]);
}

export default function DmDrawerHost() {
  const location = useLocation();
  const { accessToken } = useAuth();
  const { selectedOrgId } = useOrgFilter();
  const { presenceByUserId } = useDmSocket();
  const {
    open,
    minimized,
    activeConversationId,
    openPanel,
    closePanel,
    selectConversation,
    toggleMinimized,
    setActiveConversationId,
  } = useDmDrawer();

  const orgId =
    selectedOrgId && selectedOrgId !== "social" ? String(selectedOrgId) : null;
  const hideForFullPage = isFullMessagesPath(location.pathname);
  const notesEnsuredRef = useRef(null);
  const prevConvRef = useRef(null);
  const idleTimerRef = useRef(null);
  const panelRef = useRef(null);

  const [newChatOpen, setNewChatOpen] = useState(false);
  const [size, setSize] = useState(() => {
    const s = loadDrawerSize();
    return clampDrawerSize(s.w, s.h);
  });
  const resizeRef = useRef(null);

  useEffect(() => {
    try {
      localStorage.setItem(DRAWER_SIZE_KEY, JSON.stringify(size));
    } catch {
      /* ignore */
    }
  }, [size]);

  const startResize = (e, edges) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startY = e.clientY;
    const startW = size.w;
    const startH = size.h;
    resizeRef.current = { startX, startY, startW, startH, edges };

    const onMove = (ev) => {
      const st = resizeRef.current;
      if (!st) return;
      let nextW = st.startW;
      let nextH = st.startH;
      // Panel is anchored bottom-right: drag left/up grows, right/down shrinks
      if (st.edges.includes("left")) {
        nextW = st.startW - (ev.clientX - st.startX);
      }
      if (st.edges.includes("top")) {
        nextH = st.startH - (ev.clientY - st.startY);
      }
      setSize(clampDrawerSize(nextW, nextH));
    };

    const onUp = () => {
      resizeRef.current = null;
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  const { conversations, loading, refresh } = useDmConversations(
    open && orgId && !hideForFullPage ? orgId : null
  );

  useEffect(() => {
    if (!open || !orgId || !accessToken || hideForFullPage) return;
    if (notesEnsuredRef.current === orgId) return;
    let cancelled = false;
    (async () => {
      try {
        const notes = await ensureDmNotes(accessToken, orgId);
        notesEnsuredRef.current = orgId;
        if (cancelled) return;
        await refresh();
        setActiveConversationId((prev) => {
          if (prev) return prev;
          return notes?.conversation_id ?? null;
        });
      } catch (err) {
        console.error("Failed to ensure notes in drawer:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    open,
    orgId,
    accessToken,
    hideForFullPage,
    refresh,
    setActiveConversationId,
  ]);

  useEffect(() => {
    notesEnsuredRef.current = null;
  }, [orgId]);

  useEffect(() => {
    if (!open || activeConversationId || !conversations?.length) return;
    const self = conversations.find((c) => c.is_self);
    const recent = conversations.find((c) => !c.is_self) || self;
    if (recent) setActiveConversationId(recent.conversation_id);
  }, [open, conversations, activeConversationId, setActiveConversationId]);

  const currentUserId = useMemo(() => {
    try {
      if (!accessToken) return null;
      const payload = JSON.parse(atob(accessToken.split(".")[1]));
      return payload?.sub || null;
    } catch {
      return null;
    }
  }, [accessToken]);

  const selected = useMemo(() => {
    if (!activeConversationId) return null;
    return (
      conversations.find(
        (c) => Number(c.conversation_id) === Number(activeConversationId)
      ) || null
    );
  }, [conversations, activeConversationId]);

  const purgeIfEmpty = async (conv) => {
    if (!accessToken || !orgId || !conv) return;
    if (conv.is_self || conv.type === "channel") return;
    if ((conv.message_count || 0) > 0 || conv.last_message) return;
    try {
      await deleteDmConversation(accessToken, orgId, conv.conversation_id);
      await refresh();
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    const prevId = prevConvRef.current;
    if (prevId && Number(prevId) !== Number(activeConversationId)) {
      const prev = conversations.find(
        (c) => Number(c.conversation_id) === Number(prevId)
      );
      purgeIfEmpty(prev);
    }
    prevConvRef.current = activeConversationId;

    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
    if (
      selected &&
      !selected.is_self &&
      selected.type !== "channel" &&
      !selected.last_message &&
      (selected.message_count || 0) === 0
    ) {
      idleTimerRef.current = setTimeout(async () => {
        await purgeIfEmpty(selected);
        setActiveConversationId(null);
      }, DM_EMPTY_IDLE_MS);
    }
    return () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeConversationId, selected?.message_count, selected?.last_message]);

  const handleClose = async () => {
    if (selected) await purgeIfEmpty(selected);
    closePanel();
  };

  const handleNewChatCreated = async (conv) => {
    await refresh();
    selectConversation(conv.conversation_id);
  };

  const handleToggleMute = async (conversation, muted) => {
    const cid = conversation?.conversation_id ?? activeConversationId;
    if (!accessToken || !orgId || !cid) return;
    try {
      await setDmMuted(accessToken, orgId, cid, muted);
      setConversationMutedLocal(cid, muted);
      await refresh();
    } catch (err) {
      console.error("Failed to toggle mute:", err);
    }
  };

  useContainDrawerScroll(panelRef, open && !minimized && !hideForFullPage);

  if (hideForFullPage || !orgId) return null;

  const messagesHref = activeConversationId
    ? `/o/${encodeURIComponent(orgId)}/messages/${activeConversationId}`
    : `/o/${encodeURIComponent(orgId)}/messages`;

  return (
    <div className="hidden md:block fixed bottom-4 right-4 z-40">
      {!open && (
        <Button
          type="button"
          size="icon"
          className="h-12 w-12 rounded-full shadow-lg"
          onClick={() => openPanel()}
          title="Open messages"
          aria-label="Open messages"
        >
          <MessageSquare className="h-5 w-5" />
        </Button>
      )}

      {open && (
        <div
          ref={panelRef}
          className={cn(
            "relative flex flex-col rounded-t-lg border border-border bg-background shadow-xl overflow-hidden overscroll-contain",
            // Hide scrollbars in the mini viewer (scroll still works)
            "scrollbar-hide [&_*]:scrollbar-hide",
            minimized && "w-72 h-11"
          )}
          style={
            minimized
              ? undefined
              : { width: size.w, height: size.h, maxWidth: "calc(100vw - 2rem)" }
          }
        >
          {!minimized && (
            <>
              <div
                className="absolute top-0 left-0 z-20 h-3 w-3 cursor-nwse-resize"
                onPointerDown={(e) => startResize(e, ["left", "top"])}
                title="Resize"
                aria-hidden
              />
              <div
                className="absolute top-0 left-3 right-0 z-10 h-1.5 cursor-ns-resize"
                onPointerDown={(e) => startResize(e, ["top"])}
                aria-hidden
              />
              <div
                className="absolute top-3 bottom-0 left-0 z-10 w-1.5 cursor-ew-resize"
                onPointerDown={(e) => startResize(e, ["left"])}
                aria-hidden
              />
            </>
          )}
          <div className="flex items-center gap-1 bg-muted/80 border-b border-border px-2 h-11 shrink-0">
            <div className="flex-1 min-w-0 px-1">
              {selected && !selected.is_self ? (
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate leading-tight">
                    {selected.type === "group" || selected.type === "channel"
                      ? selected.title ||
                        (selected.type === "channel" ? "Channel" : "Group")
                      : getFullName(selected.peer) || "Chat"}
                  </p>
                  {selected.type === "group" ? (
                    <p className="text-[11px] text-muted-foreground truncate">
                      {selected.member_count || selected.members?.length || 0}{" "}
                      members
                    </p>
                  ) : selected.type === "channel" ? (
                    <p className="text-[11px] text-muted-foreground truncate">
                      Channel
                    </p>
                  ) : (
                    (() => {
                      const p = resolvePresence(selected.peer, presenceByUserId);
                      return (
                        <p className="text-[11px] text-muted-foreground flex items-center gap-1 truncate">
                          <span
                            className={cn(
                              "h-1.5 w-1.5 rounded-full shrink-0",
                              p.dot
                            )}
                          />
                          {p.label}
                        </p>
                      );
                    })()
                  )}
                </div>
              ) : selected?.is_self ? (
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">You</p>
                  <p className="text-[11px] text-muted-foreground truncate">
                    Notes
                  </p>
                </div>
              ) : (
                <span className="text-sm font-medium truncate">Messages</span>
              )}
            </div>
            {!minimized && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  title="New chat"
                  onClick={() => setNewChatOpen(true)}
                >
                  <SquarePen className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  asChild
                  title="Open in Messages"
                >
                  <Link to={messagesHref}>
                    <Maximize2 className="h-3.5 w-3.5" />
                  </Link>
                </Button>
              </>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={toggleMinimized}
              title={minimized ? "Expand" : "Minimize"}
            >
              <Minus className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={handleClose}
              title="Close"
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>

          {!minimized && (
            <div className="flex flex-1 min-h-0">
              <aside
                className={cn(
                  "shrink-0 border-r border-border flex flex-col min-h-0 bg-muted/10",
                  size.w < 520 ? "w-[148px]" : "w-[220px]"
                )}
              >
                <DmConversationSections
                  conversations={conversations}
                  loading={loading}
                  selectedId={activeConversationId}
                  onSelect={async (c) => {
                    if (
                      selected &&
                      Number(selected.conversation_id) !==
                        Number(c.conversation_id)
                    ) {
                      await purgeIfEmpty(selected);
                    }
                    selectConversation(c.conversation_id);
                  }}
                  onToggleMute={handleToggleMute}
                  listClassName="text-xs"
                />
              </aside>
              <main className="flex-1 min-w-0 min-h-0">
                {activeConversationId ? (
                  <DmThread
                    orgId={orgId}
                    conversationId={activeConversationId}
                    peer={selected?.peer}
                    isSelf={!!selected?.is_self}
                    conversation={selected}
                    compact
                    showPeerHeader={false}
                    onConversationChange={(next) => {
                      if (next?.left) {
                        setActiveConversationId(null);
                        refresh();
                        return;
                      }
                      if (next?.conversation_id && next.navigate) {
                        const purgeId = next.purge_conversation_id;
                        setActiveConversationId(Number(next.conversation_id));
                        (async () => {
                          if (purgeId && accessToken && orgId) {
                            try {
                              await deleteDmConversation(
                                accessToken,
                                orgId,
                                purgeId
                              );
                            } catch {
                              /* still has messages or already gone */
                            }
                          }
                          await refresh();
                        })();
                        return;
                      }
                      refresh();
                    }}
                  />
                ) : (
                  <div className="h-full flex items-center justify-center text-xs text-muted-foreground p-4">
                    Select a conversation
                  </div>
                )}
              </main>
            </div>
          )}
        </div>
      )}

      <NewDmChatDialog
        open={newChatOpen}
        onOpenChange={setNewChatOpen}
        orgId={orgId}
        accessToken={accessToken}
        currentUserId={currentUserId}
        onCreated={handleNewChatCreated}
      />
    </div>
  );
}
